import { Card, CardContent } from "@/components/ui/card";
import { usePlatformCopy } from "@/hooks/usePlatformCopy";
import { COPY } from "@/lib/copy";
import { Scale, ShieldCheck, AlertTriangle } from "lucide-react";
import { BallotWarning } from "@/components/BallotWarning";

export default function Legal() {
  const copy = usePlatformCopy();
  const d = copy.disclaimers || {};
  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-3xl">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="legal-title">Legal &amp; disclaimers</h1>
        <p className="text-muted-foreground mt-3">Please read these statements carefully. They apply to every page and every feature of this platform.</p>
      </div>

      <div className="mt-10 max-w-3xl space-y-5">
        <Card><CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-2.5 mb-4"><ShieldCheck className="h-5 w-5 text-primary" /><h2 className="font-heading font-semibold text-lg">Core disclaimers</h2></div>
          <div className="space-y-4">
            {[d.independent || COPY.disclaimers.independent,
              d.not_replacement || COPY.disclaimers.notReplacement,
              d.voluntary || COPY.disclaimers.voluntary].map((t, i) => (
              <blockquote key={i} className="border-l-4 border-primary/30 pl-4 text-sm text-foreground leading-relaxed" data-testid={`legal-disclaimer-${i + 1}`}>
                {t}
              </blockquote>
            ))}
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-2.5 mb-4"><Scale className="h-5 w-5 text-accent" /><h2 className="font-heading font-semibold text-lg">Independence and governance</h2></div>
          <blockquote className="border-l-4 border-accent/40 pl-4 text-sm text-foreground leading-relaxed" data-testid="legal-governance">
            {d.governance || COPY.disclaimers.governance}
          </blockquote>
          <p className="text-sm text-muted-foreground leading-relaxed mt-4">
            Candidate participation is approved on equal-access criteria only: the applicant is a legitimate registered
            candidate on the official candidate list and agrees to the platform rules. Approval is never based on political
            beliefs, party affiliation, or paper-ballot position. Applications and decisions are preserved with an audit
            trail and are never silently deleted.
          </p>
        </CardContent></Card>

        <Card><CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-2.5 mb-4"><AlertTriangle className="h-5 w-5 text-amber-600" /><h2 className="font-heading font-semibold text-lg">What this platform does not do</h2></div>
          <ul className="space-y-2.5 text-sm text-foreground leading-relaxed list-disc pl-5">
            <li>It does not conduct an election or a parallel election, and it does not collect ballots.</li>
            <li>It does not claim to conclusively prove election fraud, and it never declares an election result invalid.</li>
            <li>It does not publish live declaration totals, leaderboards, or candidate comparisons while voting is open.</li>
            <li>It does not give candidates access to declarant identities, individual declarations, or voter data.</li>
            <li>Its electronic declarations are not commissioned affidavits unless and until a proper commissioning process is completed with authorized professionals.</li>
            <li>Any apparent discrepancy identified after voting closes must be reviewed through the proper legal and election processes — including statutory recounts and the courts.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent className="p-6 sm:p-8">
          <h2 className="font-heading font-semibold text-lg mb-4">Ballot secrecy</h2>
          <BallotWarning testId="legal-ballot-warning" />
          <p className="text-sm text-muted-foreground leading-relaxed mt-4">
            The secrecy of your ballot is protected by law. Witnessing your vote is a voluntary statement about your own
            vote; it never requires proof, and this platform will refuse and quarantine any attempted upload of ballot
            imagery or voting credentials.
          </p>
        </CardContent></Card>

        <Card className="bg-secondary"><CardContent className="p-6">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Before public launch, this platform and its declaration wording will be reviewed by an Ontario
            municipal-election lawyer, a privacy professional, a cybersecurity professional, and an authorized
            commissioner/notary for any commissioned-declaration process. Questions: legal@witnessyourvote.ca
          </p>
        </CardContent></Card>
      </div>
    </div>
  );
}
